import axios from "axios";
import { useContext, useState } from "react";
import bilAPI from "../../services/api";
import TokenService from "../../services/token.service";
import { BASE_URL } from "../constants/constants";
import { UserContext } from "../contexts/UserContext";
import useOrgScopeStore from "../stores/useOrgScopeStore";
import useSupportStore from "../stores/useSupportStore";
import { getFederatedLogoutUrl } from "../../services/federatedService";
import { clearObservationDrafts } from "../helpers/observationDraft";

export default function useAuth() {
  const { user, setUser } = useContext(UserContext);
  const [error, setError] = useState(null);

  const setUserContext = async (token) => {
    const response = await axios.get(`${BASE_URL}/auth/me/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUser(response.data);
    return response.data;
  };

  const loginUser = async ({ username, password }) => {
    setError(null);
    try {
      const response = await axios.post(`${BASE_URL}/auth/login/`, {
        username,
        password,
      });
      TokenService.updateLocalAccessToken(response.data.access);
      TokenService.updateLocalRefreshToken(response.data.refresh);
      bilAPI.defaults.headers.Authorization = `Bearer ${response.data.access}`;
      const userData = await setUserContext(response.data.access);
      return { user: userData, redirectPath: "/" };
    } catch (err) {
      setError(err.response);
      throw err;
    }
  };

  // Returns the provider URL the caller must navigate to, or null. Async
  // because the question "did this person sign in through a provider" is the
  // server's to answer, and it has to be asked while the token still works.
  const logoutUser = async () => {
    // Signing out locally must not depend on the provider being reachable, so
    // a failure here is swallowed: worst case the provider session outlives
    // this one, which is exactly today's behaviour.
    let logoutUrl = null;
    try {
      const data = await getFederatedLogoutUrl();
      logoutUrl = data?.logout_url ?? null;
    } catch {
      logoutUrl = null;
    }

    // What outlives the document, cleared either way. Observation drafts go
    // with the tokens — every account's, since this browser may be a shared
    // staff-room computer and they carry private notes (see observationDraft).
    TokenService.removeTokens();
    clearObservationDrafts();
    delete bilAPI.defaults.headers.Authorization;

    // The rest is in memory, and dropping it re-renders the whole routed tree
    // as logged-out. On the way to a provider page that is about to replace
    // the document anyway, that buys nothing and costs a painted frame of the
    // landing page — the blink between clicking "sign out" and arriving at the
    // provider. So it only happens when this tab is where the person stays,
    // and then it is what moves them off the page they were on.
    if (!logoutUrl) {
      setUser(null);
      // The org-scope selection + cached subtree are per-session: clear them so
      // the next user on this tab doesn't inherit a school outside their scope.
      // (Navigating away clears them by ending the JS context outright.)
      useOrgScopeStore.getState().reset();
      // Same for the support badge: the next person here has their own count.
      useSupportStore.getState().reset();
    }

    return logoutUrl;
  };

  return { user, loginUser, logoutUser, setUserContext, error };
}
